import { OrderTypeEnum } from "@/constant/order";
import { useAddressStore } from "@/store/addressStore";
import { useShopStore } from "@/store/shopStore";
import { useUserStore } from "@/store/userStore";
import { DistanceConverter } from "@/utils/DistanceConverter";
import GoogleMapHelper from "@/utils/googleMapHelper";
import { isDef } from "@/utils/isUtil";
import { computed, ref, watchEffect } from "vue";

export function useDeliveryFeeAndTime(isNeedWatchEffect = false) {
  const mapHelper = new GoogleMapHelper();
  const userStore = useUserStore();
  const shopStore = useShopStore();
  const addressStore = useAddressStore();
  const deliveryInfo = computed(() => ({
    lat: addressStore.getSelectedAddress?.location?.coordinate?.latitude,
    lng: addressStore.getSelectedAddress?.location?.coordinate?.longitude,
    detail: addressStore.getSelectedAddress?.location?.detail,
  }));
  const isUseMiles = computed(() => shopStore.getCountry === "US");
  const startingPrice = computed(
    () => shopStore.getShopData?.self_delivery_setting?.starting_price || 0
  ); // 旧字段，新加坡使用；新字段放在distanceSetting里
  const distanceSetting = computed(
    () => shopStore.getShopData?.self_delivery_setting?.distance_setting || []
  );
  // const deliveryFeeMethod =
  //   shopStore.getShopData?.self_delivery_setting?.delivery_fee_method ||
  //   DELIVERY_FEE_METHOD.MERCHANT;
  const orderType = computed(() => userStore.getOrderType);

  function _getDefaultDeliveryFeeAndTime() {
    return {
      fee: 0,
      time: 0,
      startingFee: 0,
      // quoteId: "",
    };
  }

  // 配送费用和配送时间计算结果
  const deliveryFeeAndTime = ref(_getDefaultDeliveryFeeAndTime());

  const shopCoordinate = computed(() =>
    isDef(shopStore.getShopData?.location?.coordinate)
      ? {
          lat: shopStore.getShopData.location.coordinate.latitude || 0,
          lng: shopStore.getShopData.location.coordinate.longitude || 0,
        }
      : null
  );

  if (isNeedWatchEffect) {
    watchEffect(() => {
      if (orderType.value === OrderTypeEnum.SELF_DELIVERY) {
        getDeliveryFeeAndTime(deliveryInfo.value).then(
          (res) => (deliveryFeeAndTime.value = res)
        );
      }
    });
  }

  /**
   * 计算用户收货地址与店铺之间的距离
   * @param {Object} userCoordinate - 用户收货地址的经纬度信息
   * @param {number} userCoordinate.lat - 纬度坐标
   * @param {number} userCoordinate.lng - 经度坐标
   */
  function _getDeliveryDistance(userCoordinate) {
    return shopCoordinate.value && userCoordinate
      ? mapHelper.computeDistanceBetween(shopCoordinate.value, userCoordinate)
      : NaN;
  }

  /**
   * 根据用户的收货地址，计算配送费用和配送时间
   * @param {Object} deliveryInfo - 用户的收货地址信息
   * @param {number} [deliveryInfo.lat] - 纬度坐标
   * @param {number} [deliveryInfo.lng] - 经度坐标
   * @returns {Promise<Object>} 包含配送费用和时间的Promise对象
   */
  function getDeliveryFeeAndTime(deliveryInfo) {
    const feeAndTimeData = _getDefaultDeliveryFeeAndTime();

    if (!(isDef(deliveryInfo.lat) && isDef(deliveryInfo.lng))) {
      return Promise.resolve(feeAndTimeData);
    }

    // 构造用户收货地址的经纬度
    const userCoordinate = {
      lat: deliveryInfo.lat || 0,
      lng: deliveryInfo.lng || 0,
    };

    // 计算店铺地址到用户地址之间的距离
    let distance = _getDeliveryDistance(userCoordinate);

    if (!isNaN(distance)) {
      if (isUseMiles.value) {
        distance = DistanceConverter.kmToMiles(distance / 1000);
      } else {
        distance = distance / 1000;
      }

      console.log(
        `店铺（自配送地址/店铺地址）经纬度: {${shopCoordinate.value?.lat}, ${shopCoordinate.value?.lng}}`
      );
      console.log(
        `用户地址经纬度: {${userCoordinate?.lat}, ${userCoordinate?.lng}}`
      );
      console.log(`距离: ${distance}${isUseMiles.value ? "miles" : "km"}`);

      // 计算配送费用和时间
      distanceSetting.value.forEach((item) => {
        if (distance >= item.distance_begin && distance < item.distance_end) {
          feeAndTimeData.fee = item.fee;
          feeAndTimeData.time = item.time;
          feeAndTimeData.startingFee =
            process.env.VUE_APP_MODE === "us_prod"
              ? item.starting_price
              : item.starting_price || startingPrice.value;
        }
      });
    }

    // 根据Uber规则收取时，获取Uber报价
    // if (deliveryFeeMethod === DELIVERY_FEE_METHOD.UBER) {
    //   return _createQuote({
    //     deliveryFeeMethod,
    //     shopId,
    //     address: deliveryInfo,
    //   })
    //     .then((res) => {
    //       return {
    //         ...feeAndTimeData,
    //         fee: res.fee,
    //         time: res.time,
    //         quoteId: res.quoteId,
    //       };
    //     })
    //     .catch((e) => {
    //       console.error(e);
    //       return feeAndTimeData;
    //     });
    // } else {
    // 根据商家规则收取
    return Promise.resolve(feeAndTimeData);
    // }
  }

  return { deliveryFeeAndTime, getDeliveryFeeAndTime };
}
